import { apiProxyRequest } from "Api/apiProxyRequest";

import {
  CounterDataPoint,
  CounterTimelineResponse,
  GaugeDataPoint,
  GaugeTimelineResponse,
  HistogramDataPoint,
  HistogramTimelineResponse,
  HistogramWarning,
  MetricDescriptor,
  MetricType,
  ServiceMetricTimelineResponse,
  ServiceMetricsListResponse
} from "./types";

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isMetricType = (value: unknown): value is MetricType =>
  value === "gauge" ||
  value === "counter" ||
  value === "sum" ||
  value === "histogram" ||
  value === "exponential_histogram";

const isOptionalNumber = (value: unknown): value is number | null | undefined =>
  value === undefined || value === null || typeof value === "number";

const isOptionalInteger = (
  value: unknown
): value is number | null | undefined => isOptionalNumber(value);

const isMetricDescriptor = (value: unknown): value is MetricDescriptor => {
  if (!isRecord(value)) return false;
  if (typeof value.metric_name !== "string") return false;
  if (typeof value.unit !== "string") return false;
  if (typeof value.description !== "string") return false;
  if (!isMetricType(value.type)) return false;
  const links = value._links;
  if (!isRecord(links)) return false;
  const metric = links.metric;
  if (!isRecord(metric)) return false;
  if (typeof metric.href !== "string") return false;
  return true;
};

const isServiceMetricsListResponse = (
  value: unknown
): value is ServiceMetricsListResponse => {
  if (!isRecord(value)) return false;
  if (typeof value._from !== "number") return false;
  if (typeof value._to !== "number") return false;
  if (typeof value._project_id !== "string") return false;
  if (typeof value._environment_id !== "string") return false;
  if (typeof value._branch_machine_name !== "string") return false;
  if (typeof value._service !== "string") return false;
  if (!Array.isArray(value.metrics)) return false;
  return value.metrics.every(isMetricDescriptor);
};

const hasTimelineEnvelope = (
  value: Readonly<Record<string, unknown>>
): boolean => {
  if (typeof value._grain !== "number") return false;
  if (typeof value._from !== "number") return false;
  if (typeof value._to !== "number") return false;
  if (typeof value._project_id !== "string") return false;
  if (typeof value._environment_id !== "string") return false;
  if (typeof value._branch_machine_name !== "string") return false;
  if (typeof value._service !== "string") return false;
  if (typeof value.metric_name !== "string") return false;
  return true;
};

const isGaugeDataPoint = (value: unknown): value is GaugeDataPoint => {
  if (!isRecord(value)) return false;
  if (typeof value.timestamp !== "number") return false;
  if (!isOptionalNumber(value.last)) return false;
  if (!isOptionalNumber(value.min)) return false;
  if (!isOptionalNumber(value.max)) return false;
  if (!isOptionalNumber(value.avg)) return false;
  if (!isOptionalInteger(value.sample_count)) return false;
  return true;
};

const isGaugeTimelineResponse = (
  value: unknown
): value is GaugeTimelineResponse => {
  if (!isRecord(value)) return false;
  if (value.type !== "gauge") return false;
  if (!hasTimelineEnvelope(value)) return false;
  if (!Array.isArray(value.data)) return false;
  return value.data.every(isGaugeDataPoint);
};

const isCounterDataPoint = (value: unknown): value is CounterDataPoint => {
  if (!isRecord(value)) return false;
  if (typeof value.timestamp !== "number") return false;
  if (!isOptionalNumber(value.value)) return false;
  if (!isOptionalInteger(value.sample_count)) return false;
  return true;
};

const isCounterTimelineResponse = (
  value: unknown
): value is CounterTimelineResponse => {
  if (!isRecord(value)) return false;
  if (value.type !== "counter") return false;
  if (!hasTimelineEnvelope(value)) return false;
  if (!Array.isArray(value.data)) return false;
  return value.data.every(isCounterDataPoint);
};

const isHistogramWarning = (value: unknown): value is HistogramWarning => {
  if (!isRecord(value)) return false;
  if (typeof value.code !== "string") return false;
  if (typeof value.message !== "string") return false;
  if (value.context !== undefined && !isRecord(value.context)) return false;
  return true;
};

const isHistogramDataPoint = (value: unknown): value is HistogramDataPoint => {
  if (!isRecord(value)) return false;
  if (typeof value.timestamp !== "number") return false;
  if (typeof value.count !== "number") return false;
  if (typeof value.sum !== "number") return false;
  if (!Array.isArray(value.bucket_counts)) return false;
  if (!value.bucket_counts.every(n => typeof n === "number")) return false;
  if (!isRecord(value.quantiles)) return false;
  for (const q of Object.values(value.quantiles)) {
    if (q !== null && typeof q !== "number") return false;
  }
  return true;
};

const isHistogramTimelineResponse = (
  value: unknown
): value is HistogramTimelineResponse => {
  if (!isRecord(value)) return false;
  if (value.type !== "histogram") return false;
  if (!hasTimelineEnvelope(value)) return false;
  if (!Array.isArray(value._quantiles)) return false;
  if (!value._quantiles.every(q => typeof q === "number")) return false;
  if (!Array.isArray(value._warnings)) return false;
  if (!value._warnings.every(isHistogramWarning)) return false;
  if (!Array.isArray(value.bucket_bounds)) return false;
  if (!value.bucket_bounds.every(b => typeof b === "number")) return false;
  if (!Array.isArray(value.data)) return false;
  return value.data.every(isHistogramDataPoint);
};

const isServiceMetricTimelineResponse = (
  value: unknown
): value is ServiceMetricTimelineResponse =>
  isGaugeTimelineResponse(value) ||
  isCounterTimelineResponse(value) ||
  isHistogramTimelineResponse(value);

export type MetricsExplorerRequestOptions = {
  readonly signal?: AbortSignal;
};

export type TimelineQueryParams = {
  readonly fromUnixSeconds: number;
  readonly toUnixSeconds: number;
  readonly grainSeconds?: number;
  readonly quantiles?: readonly number[];
};

const buildListUrl = (
  projectId: string,
  environmentId: string,
  service: string,
  params: { fromUnixSeconds: number; toUnixSeconds: number }
): string => {
  const encodedProjectId = encodeURIComponent(projectId);
  const encodedEnvironmentId = encodeURIComponent(environmentId);
  const encodedService = encodeURIComponent(service);
  const query = new URLSearchParams({
    from: params.fromUnixSeconds.toString(),
    to: params.toUnixSeconds.toString()
  });
  return `/projects/${encodedProjectId}/environments/${encodedEnvironmentId}/observability/metrics/${encodedService}?${query.toString()}`;
};

const buildTimelineUrl = (
  metricHref: string,
  params: TimelineQueryParams
): string => {
  const separator = metricHref.includes("?") ? "&" : "?";
  const query = new URLSearchParams({
    from: params.fromUnixSeconds.toString(),
    to: params.toUnixSeconds.toString()
  });
  if (params.grainSeconds !== undefined) {
    query.set("grain", params.grainSeconds.toString());
  }
  if (params.quantiles !== undefined) {
    for (const q of params.quantiles) {
      query.append("quantiles[]", q.toString());
    }
  }
  return `${metricHref}${separator}${query.toString()}`;
};

/**
 * Functions for the admin-pipeline Observability Metrics API.
 *
 * Endpoints are reached through the main API proxy under
 * `/projects/{projectId}/environments/{environmentId}/observability/metrics/…`
 * (same pattern as logs / http-metrics / resources — `api.upsun.com` rewrites
 * the admin-pipeline's `/platformsh-api/` prefix to `/projects/…`). The list
 * response returns HATEOAS `_links.metric.href` for each metric; the timeline
 * query follows that href instead of rebuilding URLs.
 */
export const listServiceMetrics = async (
  args: {
    readonly projectId: string;
    readonly environmentId: string;
    readonly service: string;
    readonly fromUnixSeconds: number;
    readonly toUnixSeconds: number;
  },
  options?: MetricsExplorerRequestOptions
): Promise<ServiceMetricsListResponse> => {
  const url = buildListUrl(args.projectId, args.environmentId, args.service, {
    fromUnixSeconds: args.fromUnixSeconds,
    toUnixSeconds: args.toUnixSeconds
  });
  const response = await apiProxyRequest<unknown>(
    url,
    "GET",
    undefined,
    {},
    0,
    {
      signal: options?.signal
    }
  );

  if (!isServiceMetricsListResponse(response)) {
    throw new Error("Invalid metrics list response");
  }
  return response;
};

export const queryServiceMetricTimeline = async (
  args: { readonly metricHref: string } & TimelineQueryParams,
  options?: MetricsExplorerRequestOptions
): Promise<ServiceMetricTimelineResponse> => {
  const url = buildTimelineUrl(args.metricHref, args);
  const response = await apiProxyRequest<unknown>(
    url,
    "GET",
    undefined,
    {},
    0,
    {
      signal: options?.signal
    }
  );

  if (!isServiceMetricTimelineResponse(response)) {
    throw new Error("Invalid metric timeline response");
  }
  return response;
};
